/**
 * Member Documents Page
 * 
 * Shows downloadable PDF documents uploaded by admin
 */

import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import apiClient from '@/utils/api';
import { toast } from 'sonner';
import MemberLayout from '@/components/MemberLayout';
import { trackEvent, USAGE_EVENTS } from '@/utils/usageTracker';
import { 
  FileText, 
  Download, 
  FolderOpen, 
  Search, 
  Filter, 
  Calendar,
  File
} from 'lucide-react';

const CATEGORIES = [
  { id: 'general', name: 'General' },
  { id: 'policy', name: 'Policies' },
  { id: 'forms', name: 'Forms' },
  { id: 'announcements', name: 'Announcements' },
  { id: 'guides', name: 'Guides & Manuals' },
  { id: 'reports', name: 'Reports' },
];

const MemberDocuments = () => {
  const [documents, setDocuments] = useState([]);
  const [notifications, setNotifications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterCategory, setFilterCategory] = useState('all');

  useEffect(() => {
    trackEvent(USAGE_EVENTS.VIEW_DOCUMENTS);
    loadData();
  }, []);

  const loadData = async () => {
    try {
      const [docsRes, notifsRes] = await Promise.all([
        apiClient.get('/documents/member'),
        apiClient.get('/notifications/my')
      ]);
      setDocuments(docsRes.data || []);
      setNotifications(notifsRes.data || []);
    } catch (error) {
      console.error('Failed to load documents:', error);
      toast.error('Failed to load documents');
    } finally {
      setLoading(false);
    }
  };

  const handleDownload = async (doc) => {
    try {
      const response = await apiClient.get(`/documents/${doc.document_id}/download`, {
        responseType: 'blob'
      });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', doc.filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      trackEvent(USAGE_EVENTS.DOWNLOAD_DOCUMENT, { document_id: doc.document_id });
      toast.success('Document downloaded successfully');
    } catch (error) {
      toast.error('Failed to download document');
    }
  };

  const formatFileSize = (bytes) => {
    if (!bytes) return 'N/A';
    if (bytes < 1024) return bytes + ' B';
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
    return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return 'N/A';
    return new Date(dateStr).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  const getCategoryName = (categoryId) => {
    const cat = CATEGORIES.find(c => c.id === categoryId);
    return cat ? cat.name : categoryId || 'General';
  };

  const filteredDocuments = documents.filter(doc => {
    const matchesSearch = doc.title?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                          (doc.description || '').toLowerCase().includes(searchTerm.toLowerCase());
    const matchesCategory = filterCategory === 'all' || doc.category === filterCategory;
    return matchesSearch && matchesCategory;
  });

  const unreadNotifications = notifications.filter(n => !n.is_read);

  // Group documents by category
  const groupedDocuments = filteredDocuments.reduce((acc, doc) => {
    const category = doc.category || 'general';
    if (!acc[category]) acc[category] = [];
    acc[category].push(doc);
    return acc;
  }, {});

  if (loading) {
    return (
      <MemberLayout currentPage="documents" title="Documents" notificationCount={0}>
        <div className="flex justify-center py-12">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
        </div>
      </MemberLayout>
    );
  }

  return (
    <MemberLayout currentPage="documents" title="Documents" notificationCount={unreadNotifications.length}>
      {/* Header */}
      <div className="mb-6">
        <h2 className="text-xl font-semibold mb-2">Organization Documents</h2>
        <p className="text-muted-foreground">
          Download important documents, forms, and resources shared by the organization.
        </p>
      </div>

      {/* Search & Filter */}
      <div className="flex flex-col sm:flex-row gap-4 mb-6">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
          <Input
            placeholder="Search documents..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="pl-9"
            data-testid="search-documents"
          />
        </div>
        <Select value={filterCategory} onValueChange={setFilterCategory}>
          <SelectTrigger className="w-full sm:w-48" data-testid="filter-category">
            <Filter className="w-4 h-4 mr-2" />
            <SelectValue placeholder="Category" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Categories</SelectItem>
            {CATEGORIES.map(cat => (
              <SelectItem key={cat.id} value={cat.id}>{cat.name}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mb-6">
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-3">
              <FolderOpen className="w-8 h-8 text-primary" />
              <div>
                <p className="text-2xl font-bold">{documents.length}</p>
                <p className="text-sm text-muted-foreground">Total</p>
              </div>
            </div>
          </CardContent>
        </Card>
        {CATEGORIES.slice(0, 3).map(cat => {
          const count = documents.filter(d => d.category === cat.id).length;
          if (count === 0) return null;
          return (
            <Card key={cat.id}>
              <CardContent className="pt-6">
                <p className="text-2xl font-bold">{count}</p>
                <p className="text-sm text-muted-foreground">{cat.name}</p>
              </CardContent>
            </Card>
          );
        })}
      </div>

      {/* Documents */}
      {filteredDocuments.length === 0 ? (
        <Card>
          <CardContent className="py-12 text-center">
            <FolderOpen className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
            <h3 className="text-lg font-medium mb-2">No documents found</h3>
            <p className="text-muted-foreground">
              {searchTerm || filterCategory !== 'all' 
                ? 'Try adjusting your search or filters'
                : 'No documents have been shared yet. Check back later!'}
            </p>
          </CardContent>
        </Card>
      ) : filterCategory === 'all' ? (
        // Show grouped by category
        <div className="space-y-6">
          {Object.entries(groupedDocuments).map(([category, docs]) => (
            <Card key={category}>
              <CardHeader>
                <CardTitle className="flex items-center gap-2 text-lg">
                  <FolderOpen className="w-5 h-5" />
                  {getCategoryName(category)}
                </CardTitle>
                <CardDescription>{docs.length} document(s)</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  {docs.map((doc) => (
                    <div 
                      key={doc.document_id} 
                      className="flex flex-col sm:flex-row justify-between items-start sm:items-center p-4 border rounded-lg gap-3 hover:bg-muted/50 transition-colors"
                      data-testid={`doc-${doc.document_id}`}
                    >
                      <div className="flex items-start gap-3 flex-1 min-w-0">
                        <div className="p-2 bg-primary/10 rounded-lg flex-shrink-0">
                          <FileText className="w-5 h-5 text-primary" />
                        </div>
                        <div className="min-w-0">
                          <p className="font-medium truncate">{doc.title}</p>
                          {doc.description && (
                            <p className="text-sm text-muted-foreground line-clamp-1">{doc.description}</p>
                          )}
                          <div className="flex flex-wrap items-center gap-3 mt-1 text-xs text-muted-foreground">
                            <span className="flex items-center gap-1">
                              <Calendar className="w-3 h-3" />
                              {formatDate(doc.uploaded_at)}
                            </span>
                            <span className="flex items-center gap-1">
                              <File className="w-3 h-3" />
                              {formatFileSize(doc.file_size)}
                            </span>
                          </div>
                        </div>
                      </div>
                      <Button 
                        size="sm" 
                        onClick={() => handleDownload(doc)}
                        data-testid={`download-${doc.document_id}`}
                      >
                        <Download className="w-4 h-4 mr-2" />
                        Download
                      </Button>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      ) : (
        // Show flat list when filtered
        <Card>
          <CardHeader>
            <CardTitle>{getCategoryName(filterCategory)}</CardTitle>
            <CardDescription>{filteredDocuments.length} document(s)</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {filteredDocuments.map((doc) => (
                <div 
                  key={doc.document_id} 
                  className="flex flex-col sm:flex-row justify-between items-start sm:items-center p-4 border rounded-lg gap-3 hover:bg-muted/50 transition-colors"
                  data-testid={`doc-${doc.document_id}`}
                >
                  <div className="flex items-start gap-3 flex-1 min-w-0">
                    <div className="p-2 bg-primary/10 rounded-lg flex-shrink-0">
                      <FileText className="w-5 h-5 text-primary" />
                    </div>
                    <div className="min-w-0">
                      <p className="font-medium truncate">{doc.title}</p>
                      {doc.description && (
                        <p className="text-sm text-muted-foreground line-clamp-1">{doc.description}</p>
                      )}
                      <div className="flex flex-wrap items-center gap-3 mt-1 text-xs text-muted-foreground">
                        <span className="flex items-center gap-1">
                          <Calendar className="w-3 h-3" />
                          {formatDate(doc.uploaded_at)}
                        </span>
                        <span className="flex items-center gap-1">
                          <File className="w-3 h-3" />
                          {formatFileSize(doc.file_size)}
                        </span>
                      </div>
                    </div>
                  </div>
                  <Button 
                    size="sm" 
                    onClick={() => handleDownload(doc)}
                    data-testid={`download-${doc.document_id}`}
                  >
                    <Download className="w-4 h-4 mr-2" />
                    Download
                  </Button>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}
    </MemberLayout>
  );
};

export default MemberDocuments;
